import { useState, type FormEvent } from 'react'

import { loginRequest, type Role, type SessionUser } from '@forbiz/contracts'

import { ApiError } from './api.js'
import { useAuth } from './auth.js'
import styles from './LoginForm.module.css'

interface LoginFormProps {
  /** 화면 부제 (예: "기업 경조사 화환 주문 관리", "운영 관리자") */
  subtitle: string
  /** 이 역할만 허용. 다른 역할이 로그인하면 거부하고 로그아웃한다. 생략 시 전체 허용. */
  allowedRoles?: readonly Role[]
  /** '비밀번호를 잊으셨나요?' 링크 주소. 주면 로그인 버튼 아래에 표시한다(web만 씀). */
  forgotPasswordHref?: string
  /** '회원가입' 링크 주소. 주면 로그인 버튼 아래에 표시한다(web만 씀 — admin은 셀프가입 없음). */
  signupHref?: string
  /** 로그인(및 역할 확인) 성공 시 호출. 앱이 여기서 이동한다. */
  onSuccess: (user: SessionUser) => void
  /** 폼 위 맥락 안내 한 줄(선택). 알림톡 링크로 튕겨 온 사용자에게 "왜 로그인인지"를 알려준다. */
  notice?: string | undefined
}

/**
 * 공유 로그인 폼. web과 admin이 함께 쓴다.
 *
 * 역할 제한은 allowedRoles로 준다. admin은 ['OPERATOR']만 넘겨,
 * 다른 역할이 로그인하면 세션을 즉시 폐기하고 에러를 보인다.
 * 진짜 방어선은 API지만, 잘못된 앱에 로그인한 사용자를 여기서도 막는다.
 */
export function LoginForm({ subtitle, allowedRoles, forgotPasswordHref, signupHref, onSuccess, notice }: LoginFormProps) {
  const { login, logout } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setFieldErrors({})
    setFormError('')

    const parsed = loginRequest.safeParse({ email, password })
    if (!parsed.success) {
      const errs: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0])
        if (key && !errs[key]) errs[key] = issue.message
      }
      setFieldErrors(errs)
      return
    }

    setSubmitting(true)
    try {
      const user = await login(parsed.data.email, parsed.data.password)
      if (allowedRoles && !allowedRoles.includes(user.role)) {
        // 잘못된 앱에 로그인. 세션을 없애고 막는다.
        await logout()
        setFormError('이 화면에 접근할 수 있는 계정이 아닙니다')
        setSubmitting(false)
        return
      }
      onSuccess(user)
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : '일시적인 오류가 발생했습니다. 잠시 후 다시 시도하세요',
      )
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit} noValidate>
        <h1 className={styles.brand}>포비즈</h1>
        <p className={styles.subtitle}>{subtitle}</p>

        {notice && (
          <p className={styles.notice} role="status">
            {notice}
          </p>
        )}

        {formError && (
          <div className={styles.formError} role="alert">
            {formError}
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="email">
            이메일
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={fieldErrors['email'] ? true : undefined}
            disabled={submitting}
          />
          {fieldErrors['email'] && <p className={styles.fieldError}>{fieldErrors['email']}</p>}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="password">
            비밀번호
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={fieldErrors['password'] ? true : undefined}
            disabled={submitting}
          />
          {fieldErrors['password'] && (
            <p className={styles.fieldError}>{fieldErrors['password']}</p>
          )}
        </div>

        <button type="submit" data-variant="primary" className={styles.submit} disabled={submitting}>
          {submitting ? '로그인 중…' : '로그인'}
        </button>

        {forgotPasswordHref && (
          <a className={styles.forgot} href={forgotPasswordHref}>
            비밀번호를 잊으셨나요?
          </a>
        )}

        {signupHref && (
          <p className={styles.signupRow}>
            계정이 없으신가요?{' '}
            <a className={styles.signupLink} href={signupHref}>
              회원가입
            </a>
          </p>
        )}
      </form>
    </div>
  )
}
